import React, { useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { usePlatformSettings } from '../../hooks/usePlatformSettings';

const BASE  = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const token = () => localStorage.getItem('kialuga_token');

function parseCSV(texto) {
  const linhas = texto.trim().split('\n');
  if (linhas.length < 2) return [];
  const cabecalhos = linhas[0].split(',').map(h => h.trim().toLowerCase()
    .replace(/[""]/g, '').replace(/\s+/g, '_'));
  return linhas.slice(1).filter(l => l.trim()).map(linha => {
    // Trata campos com vírgulas dentro de aspas
    const valores = [];
    let campo = '', dentroAspas = false;
    for (const c of linha) {
      if (c === '"' || c === '“' || c === '”') { dentroAspas = !dentroAspas; }
      else if (c === ',' && !dentroAspas) { valores.push(campo.trim()); campo = ''; }
      else { campo += c; }
    }
    valores.push(campo.trim());
    return cabecalhos.reduce((obj, h, i) => { obj[h] = valores[i] || ''; return obj; }, {});
  });
}

const PASSOS = ['upload', 'preview', 'publicando', 'concluido'];

export default function BulkUpload() {
  const navigate = useNavigate();
  const { settings } = usePlatformSettings();
  const maxProdutos  = settings.upload_massa_max_produtos ?? 50;

  const [passo,     setPasso]     = useState('upload');
  const [produtos,  setProdutos]  = useState([]);
  const [errosVal,  setErrosVal]  = useState([]);
  const [erroPubl,  setErroPubl]  = useState('');
  const [publicados, setPublicados] = useState(0);
  const [statusMsg,  setStatusMsg]  = useState('');

  const handleFicheiro = useCallback((ficheiro) => {
    const reader = new FileReader();
    reader.onload = e => {
      const parsed = parseCSV(e.target.result);
      setProdutos(parsed);
      setErrosVal([]);
      setErroPubl('');
      setPasso('preview');
    };
    reader.readAsText(ficheiro, 'UTF-8');
  }, []);

  const handleDrop = e => {
    e.preventDefault();
    const f = e.dataTransfer.files[0];
    if (f && f.name.endsWith('.csv')) handleFicheiro(f);
  };

  const publicar = async () => {
    setPasso('publicando');
    setErroPubl('');
    try {
      const res = await fetch(`${BASE}/anuncios/bulk-upload`, {
        method:  'POST',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token()}` },
        body:    JSON.stringify({ produtos }),
      });
      const data = await res.json();
      if (!res.ok) {
        if (data.erros_validacao) {
          setErrosVal(data.erros_validacao);
          setPasso('preview');
        } else {
          setErroPubl(data.mensagem || 'Erro ao publicar');
          setPasso('preview');
        }
        return;
      }
      setPublicados(data.inseridos);
      setStatusMsg(data.mensagem);
      setPasso('concluido');
    } catch {
      setErroPubl('Erro de ligação. Tenta novamente.');
      setPasso('preview');
    }
  };

  // ── Ecrã: Upload ───────────────────────────────────────────────────────────
  if (passo === 'upload') return (
    <div style={{ maxWidth: '520px', margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ textAlign: 'center', marginBottom: '28px' }}>
        <div style={{ width: '56px', height: '56px', background: 'var(--success-bg)',
          borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
          margin: '0 auto 12px', fontSize: '24px' }}>📦</div>
        <h1 style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
          Publicação em massa
        </h1>
        <p style={{ fontSize: '13px', color: 'var(--text-secondary)', marginTop: '4px' }}>
          Publica até {maxProdutos} produtos de uma vez com um ficheiro CSV.
        </p>
      </div>

      {/* Download template */}
      <a href="/templates/kialuga-bulk-upload-template.csv" download
        style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '14px 16px',
          border: '1px solid var(--success-border, #bbf7d0)', background: 'var(--success-bg)',
          borderRadius: '14px', marginBottom: '16px', textDecoration: 'none', color: 'inherit' }}>
        <span style={{ fontSize: '20px' }}>📥</span>
        <div>
          <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--success)', margin: 0 }}>
            Download do template CSV
          </p>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
            Preenche o template e faz o upload aqui · Guarda em UTF-8
          </p>
        </div>
      </a>

      {/* Zona de drop */}
      <label
        onDragOver={e => e.preventDefault()}
        onDrop={handleDrop}
        style={{ display: 'block', cursor: 'pointer' }}>
        <div style={{ border: '2px dashed var(--border)', borderRadius: '16px',
          padding: '48px 24px', textAlign: 'center',
          transition: 'border-color .2s, background .2s' }}
          onMouseEnter={e => { e.currentTarget.style.borderColor = 'var(--success)'; e.currentTarget.style.background = 'var(--success-bg)'; }}
          onMouseLeave={e => { e.currentTarget.style.borderColor = 'var(--border)'; e.currentTarget.style.background = 'transparent'; }}>
          <div style={{ fontSize: '32px', marginBottom: '8px' }}>📄</div>
          <p style={{ fontSize: '14px', fontWeight: '600', color: 'var(--text-primary)', margin: '0 0 4px' }}>
            Selecciona ou arrasta o ficheiro CSV
          </p>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: 0 }}>
            Máximo {maxProdutos} produtos · Formato UTF-8
          </p>
        </div>
        <input type="file" accept=".csv" className="hidden"
          style={{ display: 'none' }}
          onChange={e => { const f = e.target.files?.[0]; if (f) handleFicheiro(f); }}/>
      </label>
    </div>
  );

  // ── Ecrã: Preview ──────────────────────────────────────────────────────────
  if (passo === 'preview') return (
    <div style={{ maxWidth: '800px', margin: '0 auto', padding: '24px 16px' }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '16px' }}>
        <h2 style={{ fontSize: '18px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
          {produtos.length} produto(s) encontrado(s)
        </h2>
        <button onClick={() => { setProdutos([]); setErrosVal([]); setPasso('upload'); }}
          style={{ background: 'none', border: 'none', cursor: 'pointer',
            fontSize: '13px', color: 'var(--text-secondary)' }}>
          ← Voltar
        </button>
      </div>

      {errosVal.length > 0 && (
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)',
          borderRadius: '14px', padding: '14px 16px', marginBottom: '16px' }}>
          <p style={{ fontSize: '13px', fontWeight: '700', color: 'var(--danger)', marginBottom: '8px' }}>
            Erros encontrados — corrige o CSV e tenta novamente:
          </p>
          {errosVal.map(ev => (
            <p key={ev.linha} style={{ fontSize: '12px', color: 'var(--danger)', margin: '2px 0' }}>
              <strong>Linha {ev.linha}:</strong> {ev.erros.join(' · ')}
            </p>
          ))}
        </div>
      )}

      {erroPubl && (
        <div style={{ background: 'var(--danger-bg)', border: '1px solid var(--danger-border)',
          borderRadius: '14px', padding: '12px 16px', marginBottom: '16px',
          fontSize: '13px', color: 'var(--danger)' }}>
          {erroPubl}
        </div>
      )}

      {/* Tabela de preview */}
      <div style={{ border: '1px solid var(--border)', borderRadius: '14px',
        overflow: 'hidden', marginBottom: '20px' }}>
        <div style={{ overflowX: 'auto', maxHeight: '320px', overflowY: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '12px' }}>
            <thead style={{ background: 'var(--bg)', position: 'sticky', top: 0 }}>
              <tr>
                {['Título', 'Categoria', 'Preço/dia', 'Condição', 'Localização'].map(h => (
                  <th key={h} style={{ padding: '10px 12px', textAlign: h === 'Preço/dia' ? 'right' : 'left',
                    fontWeight: '600', color: 'var(--text-secondary)', fontSize: '11px',
                    textTransform: 'uppercase', whiteSpace: 'nowrap', borderBottom: '1px solid var(--border)' }}>
                    {h}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {produtos.map((p, i) => (
                <tr key={i} style={{ borderTop: i > 0 ? '1px solid var(--border)' : 'none' }}>
                  <td style={{ padding: '10px 12px', color: 'var(--text-primary)',
                    maxWidth: '200px', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {p.titulo}
                  </td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{p.categoria}</td>
                  <td style={{ padding: '10px 12px', textAlign: 'right', fontWeight: '600',
                    color: 'var(--text-primary)' }}>${p.preco_por_dia_usd}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{p.condicao}</td>
                  <td style={{ padding: '10px 12px', color: 'var(--text-secondary)' }}>{p.localizacao}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <button onClick={publicar} disabled={errosVal.length > 0}
        style={{ width: '100%', padding: '14px', borderRadius: '14px', border: 'none',
          cursor: errosVal.length > 0 ? 'not-allowed' : 'pointer',
          background: errosVal.length > 0 ? 'var(--border)' : 'var(--success)',
          color: errosVal.length > 0 ? 'var(--text-secondary)' : '#fff',
          fontSize: '15px', fontWeight: '700' }}>
        Publicar {produtos.length} produto(s) →
      </button>
    </div>
  );

  // ── Ecrã: Publicando ───────────────────────────────────────────────────────
  if (passo === 'publicando') return (
    <div style={{ textAlign: 'center', padding: '80px 24px' }}>
      <div style={{ fontSize: '40px', marginBottom: '16px',
        animation: 'pulse 1.5s infinite' }}>📦</div>
      <p style={{ fontSize: '15px', fontWeight: '600', color: 'var(--text-primary)' }}>
        A publicar {produtos.length} produto(s)...
      </p>
    </div>
  );

  // ── Ecrã: Concluído ────────────────────────────────────────────────────────
  return (
    <div style={{ textAlign: 'center', padding: '60px 24px', maxWidth: '480px', margin: '0 auto' }}>
      <div style={{ width: '80px', height: '80px', background: 'var(--success-bg)',
        borderRadius: '50%', display: 'flex', alignItems: 'center', justifyContent: 'center',
        margin: '0 auto 20px', fontSize: '36px' }}>✅</div>
      <h2 style={{ fontSize: '22px', fontWeight: '700', color: 'var(--text-primary)', marginBottom: '8px' }}>
        {publicados} produto(s) publicados!
      </h2>
      <p style={{ fontSize: '14px', color: 'var(--text-secondary)', marginBottom: '28px' }}>
        {statusMsg}
      </p>
      <div style={{ display: 'flex', gap: '12px', justifyContent: 'center' }}>
        <button onClick={() => navigate('/fornecedor/anuncios')}
          style={{ padding: '12px 24px', borderRadius: '12px', border: 'none',
            cursor: 'pointer', background: 'var(--success)', color: '#fff',
            fontSize: '14px', fontWeight: '700' }}>
          Ver meus anúncios
        </button>
        <button onClick={() => { setPasso('upload'); setProdutos([]); }}
          style={{ padding: '12px 24px', borderRadius: '12px',
            border: '1px solid var(--border)', cursor: 'pointer',
            background: 'var(--card)', color: 'var(--text-primary)',
            fontSize: '14px', fontWeight: '600' }}>
          Novo upload
        </button>
      </div>
    </div>
  );
}
